import { useState } from 'react';
import { Button, Drawer, Input, Segmented, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { IconBulb, IconDeviceFloppy, IconSend } from '@tabler/icons-react';
import { INQUIRY_FIELDS, INQUIRY_PARTNERS, INQUIRY_SUGGEST, INQUIRY_SVC, inquiryMail, type InquiryLang, type InquirySvc } from './inquiry';
import './InquiryDrawer.i18n';
import s from './inquiry.module.css';


/** Ngăn kéo "Tạo yêu cầu hỏi giá đối tác / đại lý" (board Mint-RateInquiry, `dw`). Lưu / gửi chờ bật ghi DB. */
export function InquiryDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [svc, setSvc] = useState<InquirySvc>('FRT');
  const [lang, setLang] = useState<InquiryLang>('EN');
  const [vals, setVals] = useState<Record<string, string>>({});
  const [pick, setPick] = useState<ReadonlySet<number>>(new Set([0, 4]));
  const fields = INQUIRY_FIELDS[svc].map(([k, v]) => [k, vals[`${svc}|${k}`] ?? v] as const);
  const mail = inquiryMail(svc, lang, fields);
  const pending = () => toast({ msg: t('iq.noWrite'), tone: 'err' });
  const toggle = (i: number) => setPick((p) => { const n = new Set(p); if (!n.delete(i)) n.add(i); return n; });

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={t('iq.title')}
      footer={
        <div className={s.dwFoot}>
          <Button icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={pending}>{t('iq.draft')}</Button>
          <Button icon={<IconSend size={16} aria-hidden="true" />} onClick={pending}>{t('iq.test')}</Button>
          <span className={s.dwGrow} />
          <Button variant="primary" icon={<IconSend size={16} aria-hidden="true" />} onClick={pending}>{t('iq.send', { n: pick.size })}</Button>
        </div>
      }
    >
      <div className={s.dwBody}>
        <div>
          <div className={s.dwLabel}>{t('iq.svc')}</div>
          <Segmented label={t('iq.svc')} kind="toggle" value={svc} onChange={setSvc} items={INQUIRY_SVC.map((x) => ({ key: x.key, label: x.label }))} />
        </div>
        <div>
          <div className={s.dwLabel}>{t('iq.langHead')}</div>
          <Segmented label={t('iq.langAria')} kind="toggle" value={lang} onChange={setLang} items={[{ key: 'VI', label: t('iq.vi') }, { key: 'EN', label: 'English' }]} />
        </div>
        <div className={s.dwFields}>
          {fields.map(([k, v]) => (
            <Input key={`${svc}|${k}`} label={k} value={v} onChange={(e) => setVals((o) => ({ ...o, [`${svc}|${k}`]: e.target.value }))} />
          ))}
        </div>
        <div>
          <div className={s.dwPickHead}>
            <span className={s.dwLabel}>{t('iq.pickHead', { n: pick.size })}</span>
            <Button variant="ghost" icon={<IconBulb size={16} aria-hidden="true" />} onClick={() => { setPick(new Set(INQUIRY_SUGGEST)); toast({ msg: t('iq.suggestMsg') }); }}>{t('iq.suggest')}</Button>
          </div>
          <div className={s.chips}>
            {INQUIRY_PARTNERS.map((k, i) => (
              <button key={k} type="button" className={s.chipBtn} aria-pressed={pick.has(i)} onClick={() => toggle(i)}>{k}</button>
            ))}
          </div>
        </div>
        <div className={s.dwPreview}>
          <div className={s.dwLabel}>{t('iq.preview')}</div>
          <b>{mail.subject}</b>
          <div className={s.dwMail}>{mail.body}</div>
        </div>
      </div>
    </Drawer>
  );
}
